import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { BookmarkIcon, BriefcaseIcon, BuildingIcon, CalendarIcon, CheckCircleIcon, FileTextIcon, GlobeIcon, HeadphonesIcon, LightbulbIcon, MailIcon, MessageSquareIcon, PhoneIcon, PlayIcon, PresentationIcon, StarIcon, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NextPage } from '../components/NextPage';
const BUSINESS_CATEGORIES = [{
  id: 'office',
  title: {
    en: 'Office Vocabulary',
    ar: 'مفردات المكتب'
  },
  description: {
    en: 'Essential words and phrases for the workplace',
    ar: 'الكلمات والعبارات الأساسية لبيئة العمل'
  },
  icon: BuildingIcon,
  words: [{
    en: 'Meeting',
    ar: 'اجتماع',
    example: 'We have a meeting at 2 PM.'
  }, {
    en: 'Deadline',
    ar: 'موعد نهائي',
    example: 'The deadline for this project is Friday.'
  }, {
    en: 'Colleague',
    ar: 'زميل',
    example: 'My colleague will help you with that.'
  }, {
    en: 'Report',
    ar: 'تقرير',
    example: 'Please submit your monthly report.'
  }, {
    en: 'Schedule',
    ar: 'جدول',
    example: 'Check your schedule for next week.'
  }]
}, {
  id: 'communication',
  title: {
    en: 'Business Communication',
    ar: 'التواصل في العمل'
  },
  description: {
    en: 'Professional phrases for emails, calls, and meetings',
    ar: 'عبارات احترافية للبريد الإلكتروني والمكالمات والاجتماعات'
  },
  icon: MessageSquareIcon,
  words: [{
    en: 'As per our discussion',
    ar: 'وفقاً لما ناقشناه',
    example: "As per our discussion, I've attached the files."
  }, {
    en: "I'm following up on",
    ar: 'أتابع بخصوص',
    example: "I'm following up on our conversation yesterday."
  }, {
    en: 'Please find attached',
    ar: 'تجدون مرفقاً',
    example: 'Please find attached the requested documents.'
  }, {
    en: "I'd like to schedule",
    ar: 'أود جدولة',
    example: "I'd like to schedule a meeting for next week."
  }, {
    en: 'Let me know if',
    ar: 'أعلمني إذا',
    example: 'Let me know if you need any clarification.'
  }]
}, {
  id: 'presentations',
  title: {
    en: 'Presentations & Meetings',
    ar: 'العروض التقديمية والاجتماعات'
  },
  description: {
    en: 'Phrases for effective presentations and productive meetings',
    ar: 'عبارات للعروض التقديمية الفعالة والاجتماعات المثمرة'
  },
  icon: PresentationIcon,
  words: [{
    en: "I'd like to welcome",
    ar: 'أود أن أرحب',
    example: "I'd like to welcome everyone to today's meeting."
  }, {
    en: "Let's move on to",
    ar: 'لننتقل إلى',
    example: "Let's move on to the next point on our agenda."
  }, {
    en: 'To summarize',
    ar: 'للتلخيص',
    example: 'To summarize, we need to focus on three key areas.'
  }, {
    en: 'Any questions?',
    ar: 'أي أسئلة؟',
    example: 'Any questions before we conclude?'
  }, {
    en: "I'd like to highlight",
    ar: 'أود أن أسلط الضوء على',
    example: "I'd like to highlight our recent achievements."
  }]
}];
const BUSINESS_SCENARIOS = [{
  title: {
    en: 'Job Interview',
    ar: 'مقابلة العمل'
  },
  description: {
    en: 'Common questions and professional responses',
    ar: 'الأسئلة الشائعة والردود الاحترافية'
  },
  icon: UserIcon
}, {
  title: {
    en: 'Email Correspondence',
    ar: 'المراسلات الإلكترونية'
  },
  description: {
    en: 'Professional email templates and phrases',
    ar: 'قوالب البريد الإلكتروني والعبارات الاحترافية'
  },
  icon: MailIcon
}, {
  title: {
    en: 'Phone Calls',
    ar: 'المكالمات الهاتفية'
  },
  description: {
    en: 'Handling business calls professionally',
    ar: 'التعامل مع المكالمات التجارية باحترافية'
  },
  icon: PhoneIcon
}, {
  title: {
    en: 'Meetings',
    ar: 'الاجتماعات'
  },
  description: {
    en: 'Leading and participating in business meetings',
    ar: 'قيادة والمشاركة في اجتماعات العمل'
  },
  icon: CalendarIcon
}, {
  title: {
    en: 'Negotiations',
    ar: 'المفاوضات'
  },
  description: {
    en: 'Key phrases for successful business negotiations',
    ar: 'العبارات الرئيسية للمفاوضات التجارية الناجحة'
  },
  icon: BriefcaseIcon
}, {
  title: {
    en: 'International Business',
    ar: 'الأعمال الدولية'
  },
  description: {
    en: 'Cross-cultural communication in business',
    ar: 'التواصل بين الثقافات في مجال الأعمال'
  },
  icon: GlobeIcon
}];
const BUSINESS_RESOURCES = [{
  title: {
    en: 'Business Vocabulary List',
    ar: 'قائمة مفردات الأعمال'
  },
  description: {
    en: '200+ essential business terms',
    ar: 'أكثر من 200 مصطلح أعمال أساسي'
  },
  icon: BookmarkIcon,
  type: 'download'
}, {
  title: {
    en: 'Email Templates',
    ar: 'قوالب البريد الإلكتروني'
  },
  description: {
    en: 'Professional templates for various situations',
    ar: 'قوالب احترافية لمختلف المواقف'
  },
  icon: FileTextIcon,
  type: 'interactive'
}, {
  title: {
    en: 'Meeting Phrases Audio',
    ar: 'تسجيلات صوتية لعبارات الاجتماعات'
  },
  description: {
    en: 'Listen to proper pronunciation of key phrases',
    ar: 'استمع إلى النطق الصحيح للعبارات الرئيسية'
  },
  icon: HeadphonesIcon,
  type: 'audio'
}, {
  title: {
    en: 'Business English Quiz',
    ar: 'اختبار الإنجليزية للأعمال'
  },
  description: {
    en: 'Test your business vocabulary knowledge',
    ar: 'اختبر معرفتك بمفردات الأعمال'
  },
  icon: CheckCircleIcon,
  type: 'interactive'
}];
export function BusinessEnglishPage() {
  const {
    isArabic
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2338">
      <Sitemap currentPage={{
      en: 'Business English',
      ar: 'الإنجليزية للأعمال'
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }]} data-id="element-2339" />
      <div className="max-w-7xl mx-auto" data-id="element-2340">
        {/* Header */}
        <div className="mb-12" data-id="element-2341">
          <div className="flex items-start gap-4 mb-6" data-id="element-2342">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2343">
              <BriefcaseIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2344" />
            </div>
            <div data-id="element-2345">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-2346">
                {isArabic ? 'الإنجليزية للأعمال' : 'Business English'}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-2347">
                {isArabic ? 'المفردات والعبارات المهنية لتعزيز مهاراتك في بيئة العمل' : 'Professional vocabulary and phrases to enhance your skills in the workplace'}
              </p>
            </div>
          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-8" data-id="element-2348">
            <div className="flex overflow-x-auto space-x-8" data-id="element-2349">
              <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'overview' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-2350">
                {isArabic ? 'نظرة عامة' : 'Overview'}
              </button>
              <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'vocabulary' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('vocabulary')} data-id="element-2351">
                {isArabic ? 'المفردات' : 'Vocabulary'}
              </button>
              <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'scenarios' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('scenarios')} data-id="element-2352">
                {isArabic ? 'سيناريوهات العمل' : 'Business Scenarios'}
              </button>
              <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'resources' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('resources')} data-id="element-2353">
                {isArabic ? 'الموارد' : 'Resources'}
              </button>
            </div>
          </div>
        </div>
        {/* Main Content */}
        {activeTab === 'overview' && <div className="space-y-12" data-id="element-2354">
            {/* Introduction */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2355">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2356">
                {isArabic ? 'تعزيز مهاراتك المهنية باللغة الإنجليزية' : 'Enhance Your Professional English Skills'}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-2357">
                {isArabic ? 'تم تصميم هذا القسم لمساعدتك على تطوير مهارات اللغة الإنجليزية المهنية اللازمة للنجاح في بيئة العمل العالمية. سواء كنت تتواصل عبر البريد الإلكتروني، أو تقدم عروضاً تقديمية، أو تشارك في اجتماعات، فإن هذه الموارد ستساعدك على التواصل بثقة واحترافية.' : "This section is designed to help you develop the professional English language skills necessary for success in a global workplace. Whether you're communicating via email, giving presentations, or participating in meetings, these resources will help you communicate with confidence and professionalism."}
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-2358">
                <div className="flex items-start gap-3" data-id="element-2359">
                  <LightbulbIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-2360" />
                  <p className="text-gray-800 dark:text-gray-200" data-id="element-2361">
                    {isArabic ? 'نصيحة: قم بإنشاء مجموعة من البطاقات التعليمية للمصطلحات والعبارات المتعلقة بمجال عملك المحدد لتحسين مفرداتك المهنية.' : 'Tip: Create a set of flashcards for terms and phrases related to your specific industry to improve your professional vocabulary.'}
                  </p>
                </div>
              </div>
            </div>
            {/* Why Business English Matters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2362">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2363">
                {isArabic ? 'أهمية الإنجليزية في بيئة العمل' : 'Why Business English Matters'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2364">
                <div className="space-y-4" data-id="element-2365">
                  <div className="flex items-start gap-3" data-id="element-2366">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-1" data-id="element-2367">
                      <GlobeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2368" />
                    </div>
                    <div data-id="element-2369">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2370">
                        {isArabic ? 'التواصل العالمي' : 'Global Communication'}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2371">
                        {isArabic ? 'الإنجليزية هي لغة الأعمال الدولية، مما يتيح التواصل عبر الثقافات والحدود.' : 'English is the language of international business, enabling communication across cultures and borders.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-2372">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-1" data-id="element-2373">
                      <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2374" />
                    </div>
                    <div data-id="element-2375">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2376">
                        {isArabic ? 'تعزيز فرص العمل' : 'Career Advancement'}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2377">
                        {isArabic ? 'تفتح مهارات الإنجليزية المهنية أبواباً لفرص وظيفية أفضل وتقدم مهني أسرع.' : 'Professional English skills open doors to better job opportunities and faster career progression.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2378">
                  <div className="flex items-start gap-3" data-id="element-2379">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-1" data-id="element-2380">
                      <MessageSquareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2381" />
                    </div>
                    <div data-id="element-2382">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2383">
                        {isArabic ? 'التواصل الاحترافي' : 'Professional Communication'}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2384">
                        {isArabic ? 'تساعدك على التعبير عن أفكارك بوضوح ودقة في بيئة العمل.' : 'Helps you express your ideas clearly and accurately in a work environment.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-2385">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-1" data-id="element-2386">
                      <BriefcaseIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2387" />
                    </div>
                    <div data-id="element-2388">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2389">
                        {isArabic ? 'الثقة في مكان العمل' : 'Workplace Confidence'}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2390">
                        {isArabic ? 'بناء الثقة عند التفاعل مع زملاء العمل والعملاء والشركاء الدوليين.' : 'Build confidence when interacting with colleagues, clients, and international partners.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Key Business English Categories */}
            <div data-id="element-2391">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2392">
                {isArabic ? 'فئات الإنجليزية للأعمال الرئيسية' : 'Key Business English Categories'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2393">
                {BUSINESS_CATEGORIES.map(category => <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2394">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2395">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2396">
                        <category.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2397" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2398">
                        {isArabic ? category.title.ar : category.title.en}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-2399">
                      {isArabic ? category.description.ar : category.description.en}
                    </p>
                    <div className="mt-2" data-id="element-2400">
                      <Link to={`/resources/business-english/${category.id}`} className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex items-center gap-1" data-id="element-2401">
                        <span data-id="element-2402">
                          {isArabic ? 'استكشف المزيد' : 'Explore more'}
                        </span>
                        <PlayIcon className="w-4 h-4" data-id="element-2403" />
                      </Link>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Business Scenarios */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8" data-id="element-2404">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2405">
                {isArabic ? 'سيناريوهات العمل الشائعة' : 'Common Business Scenarios'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2406">
                {BUSINESS_SCENARIOS.map((scenario, index) => <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-2407">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2408">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2409">
                        <scenario.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2410" />
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-2411">
                        {isArabic ? scenario.title.ar : scenario.title.en}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2412">
                      {isArabic ? scenario.description.ar : scenario.description.en}
                    </p>
                    <Link to="#" className="text-blue-600 dark:text-blue-400 hover:underline text-sm" data-id="element-2413">
                      {isArabic ? 'تعلم المزيد' : 'Learn more'}
                    </Link>
                  </div>)}
              </div>
            </div>
          </div>}
        {activeTab === 'vocabulary' && <div className="space-y-12" data-id="element-2414">
            {BUSINESS_CATEGORIES.map(category => <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-id="element-2415">
                <div className="p-6" data-id="element-2416">
                  <div className="flex items-center gap-3 mb-6" data-id="element-2417">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2418">
                      <category.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2419" />
                    </div>
                    <div data-id="element-2420">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2421">
                        {isArabic ? category.title.ar : category.title.en}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2422">
                        {isArabic ? category.description.ar : category.description.en}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-2423">
                    {category.words.map((word, i) => <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2424">
                        <div className="flex items-center justify-between mb-2" data-id="element-2425">
                          <div className="font-medium text-gray-900 dark:text-white text-lg" data-id="element-2426">
                            {word.en}
                          </div>
                          <div className="text-blue-600 dark:text-blue-400 text-lg" dir="rtl" data-id="element-2427">
                            {word.ar}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300 italic" data-id="element-2428">
                          "{word.example}"
                        </div>
                        <button className="mt-2 p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full" data-id="element-2429">
                          <HeadphonesIcon className="w-4 h-4" data-id="element-2430" />
                        </button>
                      </div>)}
                  </div>
                  <div className="mt-6 text-center" data-id="element-2431">
                    <Link to={`/resources/business-english/${category.id}`} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors" data-id="element-2432">
                      <span data-id="element-2433">
                        {isArabic ? 'عرض المزيد من المفردات' : 'View more vocabulary'}
                      </span>
                      <PlayIcon className="w-4 h-4" data-id="element-2434" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>}
        {activeTab === 'scenarios' && <div className="space-y-8" data-id="element-2435">
            <div className="grid grid-cols-1 gap-8" data-id="element-2436">
              {/* Job Interview Scenario */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2437">
                <div className="flex items-center gap-3 mb-6" data-id="element-2438">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2439">
                    <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2440" />
                  </div>
                  <div data-id="element-2441">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2442">
                      {isArabic ? 'مقابلة العمل' : 'Job Interview'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2443">
                      {isArabic ? 'الأسئلة الشائعة والردود الاحترافية' : 'Common questions and professional responses'}
                    </p>
                  </div>
                </div>
                <div className="space-y-6" data-id="element-2444">
                  {[{
                question: 'Tell me about yourself',
                questionAr: 'أخبرني عن نفسك',
                response: "I'm a [profession] with [X] years of experience in [industry]. I specialize in [skills] and have a proven track record of [achievements]. I'm particularly interested in this role because...",
                responseAr: 'أنا [المهنة] ولدي [X] سنوات من الخبرة في [الصناعة]. أنا متخصص في [المهارات] ولدي سجل حافل من [الإنجازات]. أنا مهتم بشكل خاص بهذا الدور لأن...'
              }, {
                question: 'What are your strengths?',
                questionAr: 'ما هي نقاط قوتك؟',
                response: 'My key strengths include [skill 1], [skill 2], and [skill 3]. For example, in my previous role, I used my [skill] to [specific achievement with measurable results].',
                responseAr: 'تشمل نقاط قوتي الرئيسية [المهارة 1] و[المهارة 2] و[المهارة 3]. على سبيل المثال، في دوري السابق، استخدمت مهارتي في [المهارة] لتحقيق [إنجاز محدد مع نتائج قابلة للقياس].'
              }, {
                question: 'Why should we hire you?',
                questionAr: 'لماذا يجب أن نوظفك؟',
                response: "Based on the job description, you need someone who can [key requirement]. With my experience in [relevant experience] and skills in [relevant skills], I've successfully [relevant achievement]. I'm confident I can bring the same success to your team.",
                responseAr: 'بناءً على الوصف الوظيفي، أنتم بحاجة إلى شخص يمكنه [المتطلب الرئيسي]. مع خبرتي في [الخبرة ذات الصلة] ومهاراتي في [المهارات ذات الصلة]، نجحت في [الإنجاز ذو الصلة]. أنا واثق من أنني يمكنني تحقيق نفس النجاح لفريقكم.'
              }].map((item, i) => <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5" data-id="element-2445">
                      <div className="mb-3" data-id="element-2446">
                        <div className="font-medium text-gray-700 dark:text-gray-300 mb-1 text-sm" data-id="element-2447">
                          {isArabic ? 'السؤال:' : 'Question:'}
                        </div>
                        <div className="text-gray-900 dark:text-white text-lg font-medium" data-id="element-2448">
                          {item.question}
                        </div>
                        <div className="text-blue-600 dark:text-blue-400 mt-1" dir="rtl" data-id="element-2449">
                          {item.questionAr}
                        </div>
                      </div>
                      <div data-id="element-2450">
                        <div className="font-medium text-gray-700 dark:text-gray-300 mb-1 text-sm" data-id="element-2451">
                          {isArabic ? 'الإجابة المقترحة:' : 'Suggested Response:'}
                        </div>
                        <div className="text-gray-800 dark:text-gray-200" data-id="element-2452">
                          "{item.response}"
                        </div>
                        <div className="text-gray-600 dark:text-gray-400 mt-2 text-sm" dir="rtl" data-id="element-2453">
                          {item.responseAr}
                        </div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-6 text-center" data-id="element-2454">
                  <Link to="/resources/business-english/job-interview" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2455">
                    <span data-id="element-2456">
                      {isArabic ? 'عرض المزيد من أسئلة المقابلات' : 'View more interview questions'}
                    </span>
                    <PlayIcon className="w-4 h-4" data-id="element-2457" />
                  </Link>
                </div>
              </div>
              {/* Email Correspondence Scenario */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2458">
                <div className="flex items-center gap-3 mb-6" data-id="element-2459">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2460">
                    <MailIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2461" />
                  </div>
                  <div data-id="element-2462">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2463">
                      {isArabic ? 'المراسلات الإلكترونية' : 'Email Correspondence'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2464">
                      {isArabic ? 'قوالب البريد الإلكتروني والعبارات الاحترافية' : 'Professional email templates and phrases'}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 mb-6" data-id="element-2465">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3" data-id="element-2466">
                    {isArabic ? 'بريد إلكتروني رسمي' : 'Formal Email Template'}
                  </h4>
                  <div className="space-y-3 text-gray-800 dark:text-gray-200" data-id="element-2467">
                    <p data-id="element-2468">Subject: [Specific and Clear Subject Line]</p>
                    <p data-id="element-2469">Dear Mr./Ms. [Last Name],</p>
                    <p data-id="element-2470">
                      I hope this email finds you well. I am writing to [state
                      purpose clearly].
                    </p>
                    <p data-id="element-2471">
                      [Main content paragraph: Provide necessary details, be
                      concise and specific]
                    </p>
                    <p data-id="element-2472">
                      [Optional second paragraph: Additional information if
                      needed]
                    </p>
                    <p data-id="element-2473">
                      Please let me know if you require any further information.
                      I look forward to your response.
                    </p>
                    <p data-id="element-2474">Thank you for your time and consideration.</p>
                    <p data-id="element-2475">Best regards,</p>
                    <p data-id="element-2476">[Your Full Name]</p>
                    <p data-id="element-2477">[Your Position]</p>
                    <p data-id="element-2478">[Your Contact Information]</p>
                  </div>
                  <div className="mt-4 text-right" data-id="element-2479">
                    <button className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors text-sm" data-id="element-2480">
                      {isArabic ? 'نسخ القالب' : 'Copy Template'}
                    </button>
                  </div>
                </div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3" data-id="element-2481">
                  {isArabic ? 'عبارات مفيدة للبريد الإلكتروني' : 'Useful Email Phrases'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-2482">
                  {[{
                en: 'I am writing to inquire about...',
                ar: 'أكتب للاستفسار عن...'
              }, {
                en: 'Thank you for your prompt response.',
                ar: 'شكراً لردكم السريع.'
              }, {
                en: 'I would like to schedule a meeting...',
                ar: 'أود جدولة اجتماع...'
              }, {
                en: 'Please find attached the requested documents.',
                ar: 'تجدون مرفقاً المستندات المطلوبة.'
              }].map((phrase, i) => <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-2483">
                      <span className="text-gray-900 dark:text-white" data-id="element-2484">
                        {phrase.en}
                      </span>
                      <span className="text-blue-600 dark:text-blue-400" dir="rtl" data-id="element-2485">
                        {phrase.ar}
                      </span>
                    </div>)}
                </div>
                <div className="text-center" data-id="element-2486">
                  <Link to="/resources/business-english/email-templates" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors" data-id="element-2487">
                    <span data-id="element-2488">
                      {isArabic ? 'عرض المزيد من قوالب البريد الإلكتروني' : 'View more email templates'}
                    </span>
                    <PlayIcon className="w-4 h-4" data-id="element-2489" />
                  </Link>
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'resources' && <div className="space-y-8" data-id="element-2490">
            {/* Resources Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2491">
              {BUSINESS_RESOURCES.map((resource, index) => <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow" data-id="element-2492">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2493">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2494">
                      <resource.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2495" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2496">
                      {isArabic ? resource.title.ar : resource.title.en}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-2497">
                    {isArabic ? resource.description.ar : resource.description.en}
                  </p>
                  <button className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${resource.type === 'download' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' : resource.type === 'audio' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'}`} data-id="element-2498">
                    {resource.type === 'download' ? <span data-id="element-2499">
                        {isArabic ? 'تنزيل المورد' : 'Download Resource'}
                      </span> : resource.type === 'audio' ? <span data-id="element-2500">{isArabic ? 'الاستماع الآن' : 'Listen Now'}</span> : <span data-id="element-2501">
                        {isArabic ? 'بدء التفاعل' : 'Start Interactive'}
                      </span>}
                  </button>
                </div>)}
            </div>
            {/* Additional Resources */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2502">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2503">
                {isArabic ? 'موارد إضافية' : 'Additional Resources'}
              </h2>
              <div className="space-y-4" data-id="element-2504">
                {[1, 2, 3].map(i => <div key={i} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2505">
                    <div className="flex items-center gap-3" data-id="element-2506">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2507">
                        {[<PresentationIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2508" />, <FileTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2509" />, <MessageSquareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2510" />][i - 1]}
                      </div>
                      <div data-id="element-2511">
                        <div className="font-medium text-gray-900 dark:text-white" data-id="element-2512">
                          {isArabic ? ['نموذج العرض التقديمي', 'مسرد مصطلحات الأعمال', 'نصائح للمكالمات الهاتفية'][i - 1] : ['Presentation Template', 'Business Terminology Glossary', 'Phone Call Tips'][i - 1]}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2513">
                          {isArabic ? ['قالب PowerPoint احترافي للعروض التقديمية', 'قائمة شاملة بمصطلحات الأعمال', 'كيفية إجراء مكالمات عمل احترافية'][i - 1] : ['Professional PowerPoint template for presentations', 'Comprehensive list of business terminology', 'How to conduct professional business calls'][i - 1]}
                        </div>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2514">
                      {isArabic ? 'الوصول' : 'Access'}
                    </button>
                  </div>)}
              </div>
            </div>
          </div>}
        {/* Next Page Navigation */}
        <div className="mt-12" data-id="element-2515">
          <NextPage currentPage={2} totalPages={2} nextPageUrl="/resources/beginners-guide" prevPageUrl="/resources/beginners-guide" nextTitle={{
          en: "Beginner's Guide",
          ar: 'دليل المبتدئين'
        }} nextDescription={{
          en: "Return to the beginner's guide for essential English basics",
          ar: 'العودة إلى دليل المبتدئين للأساسيات الإنجليزية الأساسية'
        }} isLastPage={true} data-id="element-2516" />
        </div>
      </div>
    </div>;
}